/**
 * Notification sound using the Web Audio API.
 * Synthesises a short pleasant "ding" — no external audio file required.
 */

let audioCtx: AudioContext | null = null

const getAudioContext = (): AudioContext => {
  if (!audioCtx) {
    audioCtx = new (
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    )()
  }

  return audioCtx
}

/**
 * Play a short notification ding (~200 ms, 800 Hz sine with fade-out).
 * Safely no-ops when AudioContext is unavailable or blocked by the browser.
 */
export const playNotificationSound = (): void => {
  try {
    const ctx = getAudioContext()

    // Resume context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      void ctx.resume()
    }

    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()

    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(800, ctx.currentTime)

    // Quick fade-out envelope
    gain.gain.setValueAtTime(0.3, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2)

    oscillator.connect(gain)
    gain.connect(ctx.destination)

    oscillator.start(ctx.currentTime)
    oscillator.stop(ctx.currentTime + 0.2)
  } catch {
    // Silently ignore — AudioContext may not be available
  }
}
